/**
 * @fileoverview Owns shared client behavior and exposes its stable module boundary.
 */

export const UNO_CARD_ATLAS_VERSION = "20260704-26cb5ec7";

export const UNO_CARD_ATLAS_SRC = `/assets/atlases/cards-uno.png?v=${UNO_CARD_ATLAS_VERSION}`;

export const UNO_CARD_ATLAS_CARD_WIDTH = 162;

export const UNO_CARD_ATLAS_CARD_HEIGHT = 227;

export const UNO_CARD_ATLAS_COLORS = ["red", "yellow", "green", "blue"];

export const UNO_CARD_ATLAS_VALUES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "draw2", "skip", "reverse", "chooseColor"];

export const UNO_CARD_ATLAS_WILD_VALUES = ["wild", "wild4", "back"];

export const UNO_CARD_ATLAS_COLUMNS = UNO_CARD_ATLAS_VALUES.length;

export const UNO_CARD_ATLAS_ROWS = UNO_CARD_ATLAS_COLORS.length + 1;

export const UNO_CARD_ATLAS_BACKGROUND_SIZE = `${UNO_CARD_ATLAS_COLUMNS * 100}% ${UNO_CARD_ATLAS_ROWS * 100}%`;

const COLOR_FILE_PREFIX = {
  red: "Red",
  yellow: "Yellow",
  green: "Green",
  blue: "Blue"
};

const VALUE_FILE_NAME = {
  draw2: "+2",
  skip: "Ban",
  reverse: "Switch",
  chooseColor: "ChooseColor"
};

const FILE_TO_KEY = new Map();

for (const color of UNO_CARD_ATLAS_COLORS) {
  for (const value of UNO_CARD_ATLAS_VALUES) {
    FILE_TO_KEY.set(`${COLOR_FILE_PREFIX[color]}-${VALUE_FILE_NAME[value] || value}.png`, { color, value });
  }
}

FILE_TO_KEY.set("Change Color.png", { value: "wild" });
FILE_TO_KEY.set("+4.png", { value: "wild4" });
FILE_TO_KEY.set("UnoBack.png", { value: "back" });

/**
 * Returns the uno card image src used by shared client behavior.
 * @param {string} cardOrId - Stable identifier of the target record or runtime entity.
 * @returns {string} The uno card image src text or identifier.
 */
export function unoCardImageSrc(cardOrId) {
  const info = unoCardAtlasInfo(cardOrId);
  if (!info) return "";
  if (info.color) return `/assets/cards/uno/${COLOR_FILE_PREFIX[info.color]}-${VALUE_FILE_NAME[info.value] || info.value}.png`;
  if (info.value === "wild") return "/assets/cards/uno/Change Color.png";
  if (info.value === "wild4") return "/assets/cards/uno/+4.png";
  if (info.value === "back") return "/assets/cards/uno/UnoBack.png";
  return "";
}

/**
 * Returns the uno card atlas info used by shared client behavior.
 * @param {string} cardOrId - Stable identifier of the target record or runtime entity.
 * @returns {unknown} The resulting uno card atlas info.
 */
export function unoCardAtlasInfo(cardOrId) {
  const key = unoCardAtlasKey(cardOrId);
  if (!key) return null;
  if (key.color) {
    const row = UNO_CARD_ATLAS_COLORS.indexOf(key.color);
    const column = UNO_CARD_ATLAS_VALUES.indexOf(key.value);
    if (column < 0 || row < 0) return null;
    return atlasInfo({ ...key, id: `${key.color}-${key.value}`, column, row });
  }

  const column = UNO_CARD_ATLAS_WILD_VALUES.indexOf(key.value);
  if (column < 0) return null;
  return atlasInfo({
    value: key.value,
    id: key.value === "back" ? "back" : `wild-${key.value}`,
    column,
    row: UNO_CARD_ATLAS_COLORS.length
  });
}

/**
 * Returns the uno card atlas style used by shared client behavior.
 * @param {string} cardOrId - Stable identifier of the target record or runtime entity.
 * @returns {unknown} The resulting uno card atlas style.
 */
export function unoCardAtlasStyle(cardOrId) {
  const info = unoCardAtlasInfo(cardOrId);
  if (!info) return "";
  return [
    `background-image:url(${UNO_CARD_ATLAS_SRC})`,
    `background-position:${formatPercent(info.positionX)} ${formatPercent(info.positionY)}`,
    `background-size:${UNO_CARD_ATLAS_BACKGROUND_SIZE}`,
    "background-repeat:no-repeat"
  ].join("; ");
}

/**
 * Applies uno card atlas style for shared client behavior. May update the supplied state or associated runtime resources.
 * @param {object} element - Structured element data required by the operation.
 * @param {string} cardOrId - Stable identifier of the target record or runtime entity.
 * @returns {boolean} Whether the uno card atlas style condition is satisfied.
 */
export function applyUnoCardAtlasStyle(element, cardOrId) {
  const info = unoCardAtlasInfo(cardOrId);
  if (!element || !info) return false;
  element.dataset.cardId = info.id;
  element.dataset.cardSrc = UNO_CARD_ATLAS_SRC;
  element.style.backgroundImage = `url("${UNO_CARD_ATLAS_SRC}")`;
  element.style.backgroundPosition = `${formatPercent(info.positionX)} ${formatPercent(info.positionY)}`;
  element.style.backgroundSize = UNO_CARD_ATLAS_BACKGROUND_SIZE;
  element.style.backgroundRepeat = "no-repeat";
  return true;
}

/**
 * Returns the uno card atlas key used by shared client behavior.
 * @param {string} cardOrId - Stable identifier of the target record or runtime entity.
 * @returns {string} The resulting uno card atlas key.
 */
function unoCardAtlasKey(cardOrId) {
  if (!cardOrId) return null;
  if (typeof cardOrId === "object") {
    if (cardOrId.value === "back") return { value: "back" };
    if (cardOrId.asset) {
      const byAsset = keyFromAsset(cardOrId.asset);
      if (byAsset) return byAsset;
    }
    if (cardOrId.wild || cardOrId.kind === "wild") return { value: cardOrId.value === "wild4" ? "wild4" : "wild" };
    if (cardOrId.color && cardOrId.value) return normalizeColorValue(cardOrId.color, cardOrId.value);
    if (cardOrId.id) return unoCardAtlasKey(cardOrId.id);
    return null;
  }

  const text = String(cardOrId);
  const byAsset = keyFromAsset(text);
  if (byAsset) return byAsset;
  if (text === "back") return { value: "back" };

  const parts = text.split("-");
  const first = parts[0]?.toLowerCase();
  if (first === "wild") return { value: parts[1] === "wild4" ? "wild4" : "wild" };
  if (UNO_CARD_ATLAS_COLORS.includes(first)) return normalizeColorValue(first, parts[1]);
  return null;
}

function keyFromAsset(asset) {
  const filename = String(asset || "").split("/").pop();
  return FILE_TO_KEY.get(filename) || null;
}

function normalizeColorValue(color, value) {
  const normalizedColor = String(color || "").toLowerCase();
  const normalizedValue = String(value || "");
  if (!UNO_CARD_ATLAS_COLORS.includes(normalizedColor)) return null;
  if (UNO_CARD_ATLAS_VALUES.includes(normalizedValue)) return { color: normalizedColor, value: normalizedValue };
  return null;
}

function atlasInfo(info) {
  return {
    ...info,
    positionX: percentForIndex(info.column, UNO_CARD_ATLAS_COLUMNS),
    positionY: percentForIndex(info.row, UNO_CARD_ATLAS_ROWS)
  };
}

function percentForIndex(index, count) {
  return count <= 1 ? 0 : (index / (count - 1)) * 100;
}

function formatPercent(value) {
  return `${Math.round(value * 10000) / 10000}%`;
}
